import React, { createContext, useContext, useEffect, useState } from "react";
import {
  FILE_UPLOAD_AND_SAVE_DATA_API,
  FILE_DOWNLOAD_DATA_API,
  FILE_UPLOAD_AND_REPLACE_DATA_API,
  FILE_LOCK_STATUS,
} from "../config/apiConfig";
import { securedFetch } from "../utils/api";
import { useUserContext } from "./UserContext";
import { FILE_TYPES, CHECK_IN, CHECK_OUT } from "../utils/constants";

const accessMap = {
  TFA_DISCOUNT_RATES_FILE: ["TFA"],
  EXOGENEOUS_INPUTS_FILE: ["TFA"],
  GLOBAL_ALM_MODELS_FILE: [], // [] is Only for Admin and Application Administrator role
  EMPYREAN_OP_DB_FILE: [],
  MODEL_ENTITY_OP_FILE: [],
  APPROVAL_FLOW_STEPS_FILE: [],
  MULTILEDGER_FX_FILE: [],
  SFTP_FILE_PREFIX_FILE: [],
};

export const lockEnabledFileTypes = [
  FILE_TYPES.GLOBAL_ALM_MODELS_FILE,
  FILE_TYPES.EMPYREAN_OP_DB_FILE,
  FILE_TYPES.MODEL_ENTITY_OP_FILE,
];

const checkForAccess = (type, selectedUserDetail) => {
  if (!selectedUserDetail) return false;

  const {
    admin,
    applicationAdministrator,
    regions = [],
  } = selectedUserDetail || {};

  if (admin === true || applicationAdministrator === true) {
    return true;
  }

  const allsfcs = regions?.flatMap((region) => region.sfcs || []);

  const allowedRoles = accessMap[type];
  if (!allowedRoles) {
    return false;
  }

  return allsfcs?.some(
    (sfc) =>
      allowedRoles.includes(sfc.businessEntityID) && sfc.writable === true,
  );
};

const FileUploadContext = createContext();

export const FileUploadProvider = ({ children }) => {
  const { loggedInUserEmail, selectedUserDetail } = useUserContext() || {};
  const [rows, setRows] = useState([]);
  const { admin, applicationAdministrator } = selectedUserDetail || {};

  const [allRows, setAllRows] = useState([
    {
      type: FILE_TYPES.TFA_DISCOUNT_RATES_FILE,
      title: "TFA Discount Rates",
      files: [],
      hasAccess: checkForAccess(
        FILE_TYPES.TFA_DISCOUNT_RATES_FILE,
        selectedUserDetail,
      ),
      accept: ".xlsx,.xls",
      isMultiFileSupport: false,
      canUploadAndUpdate: true,
      canUploadAndReplace: false,
      uploading: false,
      isDownloadable: false,
      progress: 0,
      error: null,
      checkInStatus: false,
      checkInBy: null,
      checkInTime: null,
      checkOutTime: null,
    },
    {
      type: FILE_TYPES.GLOBAL_ALM_MODELS_FILE,
      title: "Global ALM Models",
      files: [],
      hasAccess: checkForAccess(
        FILE_TYPES.GLOBAL_ALM_MODELS_FILE,
        selectedUserDetail,
      ),
      accept: ".xlsx,.xls",
      isMultiFileSupport: false,
      canUploadAndUpdate: true,
      canUploadAndReplace: admin || applicationAdministrator,
      uploading: false,
      isDownloadable: true,
      downloadFormat: ".xlsx",
      progress: 0,
      error: null,
      checkInStatus: false,
      checkInBy: null,
      checkInTime: null,
      checkOutTime: null,
    },
    {
      type: FILE_TYPES.EXOGENEOUS_INPUTS_FILE,
      title: "Exogenous Inputs",
      files: [],
      hasAccess: checkForAccess(
        FILE_TYPES.EXOGENEOUS_INPUTS_FILE,
        selectedUserDetail,
      ),
      accept: ".xlsx,.xls",
      isMultiFileSupport: false,
      canUploadAndUpdate: true,
      canUploadAndReplace: admin || applicationAdministrator,
      uploading: false,
      isDownloadable: true,
      downloadFormat: ".xlsx",
      progress: 0,
      error: null,
      checkInStatus: false,
      checkInBy: null,
      checkInTime: null,
      checkOutTime: null,
    },
    {
      type: FILE_TYPES.EMPYREAN_OP_DB_FILE,
      title: "Empyrean OP DB",
      files: [],
      hasAccess: checkForAccess(
        FILE_TYPES.EMPYREAN_OP_DB_FILE,
        selectedUserDetail,
      ),
      accept: ".xlsx,.xls",
      isMultiFileSupport: false,
      canUploadAndUpdate: true,
      canUploadAndReplace: admin || applicationAdministrator,
      uploading: false,
      isDownloadable: true,
      downloadFormat: ".xlsx",
      progress: 0,
      error: null,
      checkInStatus: false,
      checkInBy: null,
      checkInTime: null,
      checkOutTime: null,
    },
    {
      type: FILE_TYPES.MODEL_ENTITY_OP_FILE,
      title: "Model Entity OP",
      files: [],
      hasAccess: checkForAccess(
        FILE_TYPES.MODEL_ENTITY_OP_FILE,
        selectedUserDetail,
      ),
      accept: ".xlsx,.xls",
      isMultiFileSupport: false,
      canUploadAndUpdate: true,
      canUploadAndReplace: admin || applicationAdministrator,
      uploading: false,
      isDownloadable: true,
      downloadFormat: ".xlsx",
      progress: 0,
      error: null,
      checkInStatus: false,
      checkInBy: null,
      checkInTime: null,
      checkOutTime: null,
    },
    {
      type: FILE_TYPES.APPROVAL_FLOW_STEPS_FILE,
      title: "Approval Flow Steps",
      files: [],
      hasAccess: checkForAccess(
        FILE_TYPES.APPROVAL_FLOW_STEPS_FILE,
        selectedUserDetail,
      ),
      accept: ".xlsx,.xls",
      isMultiFileSupport: false,
      canUploadAndUpdate: true,
      canUploadAndReplace: false,
      uploading: false,
      isDownloadable: true,
      downloadFormat: ".xlsx",
      progress: 0,
      error: null,
      checkInStatus: false,
      checkInBy: null,
      checkInTime: null,
      checkOutTime: null,
    },
    {
      type: FILE_TYPES.MULTILEDGER_FX_FILE,
      title: "Multiledger FX",
      files: [],
      hasAccess: checkForAccess(
        FILE_TYPES.MULTILEDGER_FX_FILE,
        selectedUserDetail,
      ),
      accept: ".xlsx,.xls",
      isMultiFileSupport: false,
      canUploadAndUpdate: true,
      canUploadAndReplace: admin || applicationAdministrator,
      uploading: false,
      isDownloadable: true,
      downloadFormat: ".xlsx",
      progress: 0,
      error: null,
      checkInStatus: false,
      checkInBy: null,
      checkInTime: null,
      checkOutTime: null,
    },
    {
      type: FILE_TYPES.SFTP_FILE_PREFIX_FILE,
      title: "SFTP File Prefix",
      files: [],
      hasAccess: checkForAccess(
        FILE_TYPES.SFTP_FILE_PREFIX_FILE,
        selectedUserDetail,
      ),
      accept: ".xlsx,.xls",
      isMultiFileSupport: false,
      canUploadAndUpdate: true,
      canUploadAndReplace: admin || applicationAdministrator,
      uploading: false,
      isDownloadable: true,
      downloadFormat: ".xlsx",
      progress: 0,
      error: null,
      checkInStatus: false,
      checkInBy: null,
      checkInTime: null,
      checkOutTime: null,
    },
    /*
    {
      type: FILE_TYPES.TREASURY_GUIDELINES_FILE,
      title: "Treasury Guidelines",
      files: [],
      hasAccess: checkForAccess(FILE_TYPES.TREASURY_GUIDELINES_FILE, selectedUserDetail),
      accept: ".pdf",
      isMultiFileSupport: true,
      canUploadAndUpdate: true,
      canUploadAndReplace: true,
      uploading: false,
      isDownloadable: true,
      downloadFormat: ".zip",
      progress: 0,
      error: null,
      checkInStatus: false,
      checkInBy: null,
      checkInTime: null,
      checkOutTime: null,
    },
    */
  ]);

  useEffect(() => {
    const accessibleRows = allRows?.filter((row) => row.hasAccess === true);
    setRows(accessibleRows);
  }, [allRows]);

  const isLockEnabledFileType = (type) => lockEnabledFileTypes.includes(type);

  const canUseRow = (row) => {
    if (!isLockEnabledFileType(row.type)) return true;
    if (!row.checkInStatus) return true;
    return row.checkInBy === loggedInUserEmail;
  };

  const refreshLockStatus = async () => {
    try {
      const results = await Promise.all(
        lockEnabledFileTypes.map(async (type) => {
          const response = await securedFetch(
            `${FILE_LOCK_STATUS}?fileType=${type}`,
          );
          if (!response.ok) {
            return {
              fileType: type,
              checkInStatus: false,
              checkInBy: null,
              checkInTime: null,
              checkOutTime: null,
            };
          }
          return response.json();
        }),
      );

      setAllRows((prev) =>
        prev.map((row) => {
          const match = results.find((r) => r.fileType === row.type);
          if (!match) return row;

          return {
            ...row,
            checkInStatus: match.checkInStatus ?? false,
            checkInBy: match.checkInBy ?? null,
            checkInTime: match.checkInTime ?? null,
            checkOutTime: match.checkOutTime ?? null,
          };
        }),
      );
    } catch (error) {
      console.error("Failed to refresh lock status", error);
    }
  };

  useEffect(() => {
    refreshLockStatus();
  }, []);

  const attachFiles = (rowIndex, files) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[rowIndex].files = files;
      updated[rowIndex].progress = 0;
      updated[rowIndex].error = null;
      return updated;
    });
  };

  const removeFiles = (rowIndex) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[rowIndex].files = [];
      updated[rowIndex].progress = 0;
      updated[rowIndex].error = null;
      return updated;
    });
  };

  const checkInFile = async (rowIndex) => {
    const row = rows[rowIndex];
    if (!row || !isLockEnabledFileType(row.type)) return;

    try {
      const response = await securedFetch(FILE_LOCK_STATUS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fileType: row.type,
          action: CHECK_IN,
          email: loggedInUserEmail,
        }),
      });

      const responseText = await response.text();

      if (!response.ok) {
        alert(responseText || "Check in failed");
        return;
      }

      const data = responseText ? JSON.parse(responseText) : null;

      setAllRows((prev) =>
        prev.map((r) =>
          r.type === row.type
            ? {
                ...r,
                checkInStatus: true,
                checkInBy: data?.checkInBy ?? loggedInUserEmail,
                checkInTime: data?.checkInTime ?? new Date().toISOString(),
                checkOutTime: null,
              }
            : r,
        ),
      );
    } catch (error) {
      console.error("Check in error:", error);
      alert("Check in failed");
    }
  };

  const checkOutFile = async (rowIndex) => {
    const row = rows[rowIndex];
    if (!row || !isLockEnabledFileType(row.type)) return;

    if (row.checkInBy !== loggedInUserEmail) {
      alert("Only the user who locked in can unlock.");
      return;
    }

    try {
      const response = await securedFetch(FILE_LOCK_STATUS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fileType: row.type,
          action: CHECK_OUT,
          email: loggedInUserEmail,
        }),
      });

      const responseText = await response.text();

      if (!response.ok) {
        alert(responseText || "Check out failed");
        return;
      }

      const data = responseText ? JSON.parse(responseText) : null;

      setAllRows((prev) =>
        prev.map((r) =>
          r.type === row.type
            ? {
                ...r,
                checkInStatus: false,
                checkOutTime: data?.checkOutTime ?? new Date().toISOString(),
              }
            : r,
        ),
      );
    } catch (error) {
      console.error("Check out error:", error);
      alert("Check out failed");
    }
  };

  /**
   * Upload file using securedFetch with optional progress tracking
   */
  const uploadFiles = async (rowIndex) => {
    const row = rows[rowIndex];
    if (!row.files || row.files.length === 0 || row.uploading) return;

    if (!canUseRow(row)) {
      alert("This file is locked in by another user.");
      return;
    }

    const formData = new FormData();

    row.files.forEach((file) => {
      formData.append("files", file);
    });

    formData.append("type", row.type);
    formData.append("uploadedBy", loggedInUserEmail || "System");

    setRows((prev) =>
      prev.map((r, i) =>
        i === rowIndex
          ? { ...r, uploading: true, progress: 0, error: null }
          : r,
      ),
    );

    securedFetch(FILE_UPLOAD_AND_SAVE_DATA_API, {
      method: "POST",
      body: formData,
    })
      .then((response) => {
        if (response.ok) {
          alert("File Uploaded Successfully!");
          setAllRows((prev) =>
            prev.map((r) =>
              r.type === row.type
                ? { ...r, uploading: false, progress: 100, files: [] }
                : r,
            ),
          );
          setRows((prev) =>
            prev.map((r, i) =>
              i === rowIndex
                ? { ...r, uploading: false, progress: 100, files: [] }
                : r,
            ),
          );
        } else {
          throw new Error("Upload failed");
        }
      })
      .catch((error) => {
        console.error("Upload error:", error.message);
        setRows((prev) =>
          prev.map((r, i) =>
            i === rowIndex
              ? { ...r, uploading: false, error: error.message }
              : r,
          ),
        );
        alert(
          "Something went wrong with the File upload! Kindly verify if that file follows the right standards.",
        );
      });
  };

  const uploadAndReplaceFiles = async (rowIndex) => {
    const row = rows[rowIndex];
    if (
      !row.files ||
      row.files.length === 0 ||
      row.uploading ||
      !row.canUploadAndReplace
    ) {
      return;
    }

    if (!canUseRow(row)) {
      alert("This file is locked in by another user.");
      return;
    }

    const formData = new FormData();

    row.files.forEach((file) => {
      formData.append("files", file);
    });

    formData.append("type", row.type);
    formData.append("title", row.title);
    formData.append("uploadedBy", loggedInUserEmail || "System");

    setRows((prev) =>
      prev.map((r, i) =>
        i === rowIndex
          ? { ...r, uploading: true, progress: 0, error: null }
          : r,
      ),
    );

    securedFetch(FILE_UPLOAD_AND_REPLACE_DATA_API, {
      method: "POST",
      body: formData,
    })
      .then((response) => {
        if (response.ok) {
          alert("File Uploaded and Replaced Successfully!");
          setAllRows((prev) =>
            prev.map((r) =>
              r.type === row.type
                ? { ...r, uploading: false, progress: 100, files: [] }
                : r,
            ),
          );

          setRows((prev) =>
            prev.map((r, i) =>
              i === rowIndex
                ? { ...r, uploading: false, progress: 100, files: [] }
                : r,
            ),
          );
        } else {
          throw new Error("Upload failed");
        }
      })
      .catch((error) => {
        console.error("Upload error:", error.message);
        setRows((prev) =>
          prev.map((r, i) =>
            i === rowIndex
              ? { ...r, uploading: false, error: error.message }
              : r,
          ),
        );
        alert(
          "Something went wrong with the File upload! Kindly verify if that file follows the right standards.",
        );
      });
  };

  const downloadFile = async (type, extension) => {
    const row = rows.find((r) => r.type === type);
    if (row && !canUseRow(row)) {
      alert("This file is locked in by another user.");
      return;
    }

    try {
      securedFetch(`${FILE_DOWNLOAD_DATA_API}?type=${type}`)
        .then(async (response) => {
          if (response.ok) {
            const blob = await response.blob();
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = `${type}${extension}`;
            link.click();
            window.URL.revokeObjectURL(link.href);
            alert(
              "File Downloaded Successfully! Please check your Downloads folder.",
            );
          }
        })
        .catch((error) => {
          console.error("Download error:", error.message);
          alert("Failed to download");
        });
    } catch (error) {
      console.error("Download error:", error.message);
    }
  };

  return (
    <FileUploadContext.Provider
      value={{
        rows,
        attachFiles,
        removeFiles,
        uploadFiles,
        uploadAndReplaceFiles,
        downloadFile,
        checkInFile,
        checkOutFile,
        refreshLockStatus,
        setRows,
      }}
    >
      {children}
    </FileUploadContext.Provider>
  );
};

export const useFileUploadContext = () => useContext(FileUploadContext);
